import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useCampusPicker } from '@/hooks/usePickers';
import { periodsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { PeriodDefinition, PeriodType } from '@/types/domain';
import { humanise } from '@/utils/format';

const TYPES: PeriodType[] = ['CLASS', 'BREAK', 'ASSEMBLY', 'ACTIVITY', 'OTHER'];

/** `HH:mm:ss` → `HH:mm`, which is what a bell schedule reads as. */
const toDisplayTime = (value: string) => value.slice(0, 5);

/**
 * A campus's bell schedule.
 *
 * Breaks and assemblies are defined alongside lessons rather than left as gaps.
 * A gap is something the timetable would happily schedule a class into, which is
 * exactly what a break is meant to prevent.
 */
export function PeriodsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const campuses = useCampusPicker();
  const [campusId, setCampusId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PeriodDefinition | null>(null);
  const [form, setForm] = useState({
    name: '',
    startTime: '',
    endTime: '',
    periodType: 'CLASS' as PeriodType,
    displayOrder: '',
  });

  const effectiveCampusId = campusId === '' ? (campuses.data?.[0]?.id ?? '') : campusId;

  const query = useQuery({
    queryKey: queryKeys.periods.forCampus(effectiveCampusId),
    queryFn: () => periodsApi.list(effectiveCampusId),
    enabled: effectiveCampusId !== '',
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        campusId: effectiveCampusId,
        name: form.name,
        startTime: form.startTime,
        endTime: form.endTime,
        periodType: form.periodType,
        displayOrder: Number(form.displayOrder),
      };
      return editing === null ? periodsApi.create(payload) : periodsApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Period defined' : 'Period updated');
      setFormOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.periods.all });
    },
    onError: (error) => handleApiError(error),
  });

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      startTime: '',
      endTime: '',
      periodType: 'CLASS',
      // Continues the sequence rather than starting at 1, since the order must
      // be unique per campus.
      displayOrder: String((query.data?.length ?? 0) + 1),
    });
    setFormOpen(true);
  };

  const openEdit = (period: PeriodDefinition) => {
    setEditing(period);
    setForm({
      name: period.name,
      startTime: toDisplayTime(period.startTime),
      endTime: toDisplayTime(period.endTime),
      periodType: period.periodType,
      displayOrder: String(period.displayOrder),
    });
    setFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Periods"
        description="The daily rhythm, per campus. Campuses genuinely start at different times, so each keeps its own."
        action={
          can(PERMISSIONS.PERIOD_MANAGE) && effectiveCampusId !== '' ? (
            <Button onClick={openNew}>Add period</Button>
          ) : undefined
        }
      />

      <Card className="p-5">
        <div className="mb-4 max-w-sm">
          <SelectField
            label="Campus"
            value={effectiveCampusId}
            onChange={(event) => setCampusId(event.target.value)}
          >
            {campuses.data?.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </SelectField>
        </div>

        {query.isPending && effectiveCampusId !== '' ? (
          <LoadingState label="Loading periods" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">
            No periods defined for this campus yet.
          </p>
        ) : (
          <ol className="divide-y divide-line">
            {query.data.map((period) => (
              <li key={period.id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-4">
                  <span className="w-24 shrink-0 font-mono text-xs text-muted">
                    {toDisplayTime(period.startTime)}–{toDisplayTime(period.endTime)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-ink">{period.name}</p>
                    <p className="text-xs text-subtle">{humanise(period.periodType)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Whether a lesson may go here at all — the distinction the
                      timetable enforces. */}
                  {period.teachable ? (
                    <Badge tone="info">Teachable</Badge>
                  ) : (
                    <Badge tone="neutral">No lessons</Badge>
                  )}
                  {period.status === 'INACTIVE' && <Badge tone="neutral">Retired</Badge>}
                  {can(PERMISSIONS.PERIOD_MANAGE) && (
                    <Button size="sm" variant="ghost" onClick={() => openEdit(period)}>
                      Edit
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing === null ? 'Add a period' : `Edit ${editing.name}`}
        description="Overlapping periods are rejected: the timetable keys its one-lesson-per-slot rule on the period, so two covering the same minutes would let a class be double-booked unnoticed."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={form.name === '' || form.startTime === '' || form.endTime === ''}
            >
              {editing === null ? 'Add period' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="e.g. Period 3, Morning break"
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Starts"
              type="time"
              required
              value={form.startTime}
              onChange={(event) => setForm({ ...form, startTime: event.target.value })}
            />
            <TextField
              label="Ends"
              type="time"
              required
              value={form.endTime}
              onChange={(event) => setForm({ ...form, endTime: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Type"
              required
              value={form.periodType}
              onChange={(event) => setForm({ ...form, periodType: event.target.value as PeriodType })}
              hint="Only CLASS and ACTIVITY accept lessons"
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanise(type)}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Order"
              type="number"
              min={1}
              required
              value={form.displayOrder}
              onChange={(event) => setForm({ ...form, displayOrder: event.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
